import React, { useEffect, useState } from 'react';
import { X, Minus } from 'lucide-react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import type { InventoryItem } from '../../../types/inventory';

type AdjustmentReason = 'physical_count' | 'damaged' | 'expired' | 'returned' | 'other';

const REASON_LABELS: Record<AdjustmentReason, string> = {
  physical_count: 'Physical count correction',
  damaged: 'Damaged / Broken',
  expired: 'Expired',
  returned: 'Returned to vendor',
  other: 'Other',
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  item: InventoryItem;
  currentOnHand: number;
  onSave: (args: {
    delta: number;
    reason: string;
    notes?: string | null;
  }) => Promise<void>;
};

export const AdjustStockModal: React.FC<Props> = ({ isOpen, onClose, item, currentOnHand, onSave }) => {
  const [quantity, setQuantity] = useState<string>('');
  const [reason, setReason] = useState<AdjustmentReason>('physical_count');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setQuantity('');
      setReason('physical_count');
      setNotes('');
      setError(null);
      setSaving(false);
    }
  }, [isOpen]);

  useEscapeKey(onClose, isOpen && !saving);

  if (!isOpen) return null;

  const parsedQty = parseInt(quantity, 10) || 0;
  const delta = -parsedQty; // Always negative for removal
  const newOnHand = currentOnHand + delta;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!quantity.trim() || parsedQty <= 0) {
        throw new Error('Enter a quantity greater than 0');
      }
      if (newOnHand < 0) {
        throw new Error(`Cannot remove ${parsedQty} units. Only ${currentOnHand} on hand.`);
      }

      const reasonText = REASON_LABELS[reason] + (notes.trim() ? `: ${notes.trim()}` : '');
      
      await onSave({
        delta,
        reason: REASON_LABELS[reason],
        notes: reasonText,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to remove stock');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 bg-black/30 dark:bg-black/50" onClick={onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <Minus className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Remove Stock</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{item.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Current stock display */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Current stock</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">{currentOnHand}</span>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity to Remove <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max={currentOnHand}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                placeholder="0"
                autoFocus
              />
              {parsedQty > 0 && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  New stock: <span className="font-medium">{newOnHand}</span>
                </p>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value as AdjustmentReason)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                {Object.entries(REASON_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Additional details..."
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || parsedQty <= 0 || newOnHand < 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Removing...' : 'Remove Stock'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
